/**
 * Route translations mapping for Spanish and English
 * Maps route names to their localized versions
 */
export const routeTranslations = {
  es: {
    'the-route': 'ferry-puerto-montt-puerto-natales-valor-reserva',
    'our-ferries': 'navimag-precios-tarifas-ferry-esperanza',
    'our-leaders': 'nuestros-lideres',
    'what-to-expect': 'que-esperar-del-viaje',
    contact: 'contacto-pasajeros-ferry-navimag',
    'public/faqs': 'preguntas-frecuentes-ferry-pasajeros',
    'blog-viajes-ferry-chile': 'blog-viajes-ferry-chile',
    guides: 'guias',
    videos: 'videos',
    galleries: 'galerias'
  },
  en: {
    'the-route': 'ferry-puerto-montt-puerto-natales-fare-booking',
    'our-ferries': 'navimag-esperanza-ferry-prices-fares',
    'our-leaders': 'team-and-board',
    'what-to-expect': 'what-to-expect-from-your-trip',
    contact: 'navimag-ferry-passenger-contact',
    'public/faqs': 'passenger-ferry-faq',
    'blog-viajes-ferry-chile': 'chile-ferry-travel-blog',
    guides: 'guides',
    videos: 'videos',
    galleries: 'galleries'
  }
};

/**
 * Get the translated route path for a given route and language
 * @param {string} route - The base route (e.g., 'the-route')
 * @param {string} language - The language code ('es' or 'en')
 * @returns {string} The translated route path
 */
export const getTranslatedRoute = (route, language = 'es') => {
  const lang = language.toLowerCase();
  const translations = routeTranslations[lang] || routeTranslations.es;
  return translations[route] || route;
};

/**
 * Get the full localized URL with language prefix
 * @param {string} route - The base route (e.g., 'the-route')
 * @param {string} language - The language code ('es' or 'en')
 * @returns {string} The full localized URL (e.g., '/es/la-ruta')
 */
export const getLocalizedUrl = (route, language = 'es') => {
  const lang = language.toLowerCase();

  // Handle empty route (home page)
  if (!route || route === '') {
    return `/${lang}`;
  }

  const translatedRoute = getTranslatedRoute(route, lang);
  return `/${lang}/${translatedRoute}`;
};

/**
 * Extract language and route from a URL path
 * @param {string} pathname - The URL pathname (e.g., '/es/la-ruta')
 * @returns {object} Object with language and route
 */
export const parseLocalizedUrl = pathname => {
  // Handle /es and /en as standalone paths (home page)
  if (pathname === '/es' || pathname === '/en') {
    return {
      language: pathname === '/es' ? 'es' : 'en',
      route: ''
    };
  }

  // Handle /es/route or /en/route
  const match = pathname.match(/^\/(es|en)\/(.+)$/);
  if (match) {
    return {
      language: match[1],
      route: match[2]
    };
  }

  // Handle blog post structure: /blog/es/slug or /blog/en/slug
  const blogPostMatch = pathname.match(/^\/blog\/(es|en)\/(.+)$/);
  if (blogPostMatch) {
    return {
      language: blogPostMatch[1],
      route: `blog/${blogPostMatch[2]}`
    };
  }

  // Handle blog listing: /blog/es or /blog/en
  const blogListingMatch = pathname.match(/^\/blog\/(es|en)$/);
  if (blogListingMatch) {
    return {
      language: blogListingMatch[1],
      route: 'blog'
    };
  }

  // Root path or other paths
  return {
    language: null,
    route: pathname.replace(/^\//, '')
  };
};

/**
 * Reverse lookup: find the base route from a translated route
 * @param {string} translatedRoute - The translated route (e.g., 'la-ruta')
 * @param {string} language - The language code ('es' or 'en')
 * @returns {string|null} The base route or null if not found
 */
export const getBaseRoute = (translatedRoute, language = 'es') => {
  const lang = language.toLowerCase();
  const translations = routeTranslations[lang] || routeTranslations.es;

  const baseRoute = Object.keys(translations).find(
    key => translations[key] === translatedRoute
  );

  return baseRoute || translatedRoute; // Return as-is if no translation found
};

/**
 * Check if a route is an old route that should be redirected
 * @param {string} route - The route to check (e.g., 'the-route')
 * @returns {boolean} True if the route should be redirected
 */
export const isOldRoute = route => {
  const oldRoutes = [
    'the-route',
    'our-ferries',
    'our-leaders',
    'what-to-expect',
    'contact',
    'public/blogs',
    'public/faqs',
    'blog/es',
    'blog/en'
  ];

  // Check if it's in the old routes list
  if (oldRoutes.includes(route)) {
    return true;
  }

  // Check if it starts with old blog structure
  if (route.startsWith('blog/es') || route.startsWith('blog/en')) {
    return true;
  }

  // Check if it's in the route translations
  const allRoutes = [
    ...Object.keys(routeTranslations.es),
    ...Object.keys(routeTranslations.en)
  ];

  return allRoutes.includes(route);
};

export const blogsIndexPath = (pathname = '') => {
  const path = String(pathname);
  const english =
    path === '/en' || path.startsWith('/en/') || path.startsWith('/blog/en');

  return english
    ? getLocalizedUrl('blog-viajes-ferry-chile', 'en')
    : getLocalizedUrl('blog-viajes-ferry-chile', 'es');
};
